/**
 * Local-time date helpers for daily modes. We deliberately use local
 * time (not UTC) — the share text shows the user's local date, and a
 * player picking up the game in any timezone gets a consistent
 * "today" across their session.
 *
 * Tradeoff: two players in different timezones may see different
 * prompts on the same wall-clock instant. Acceptable for a casual
 * daily; the share text carries the date for context.
 */

/** `YYYY-MM-DD` for the user's current local date — RNG seed input. */
export function localDateString(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Locale-formatted date for display on share text + end-screen ("April 24"). */
export function formatLocalDateLabel(now: Date = new Date()): string {
  return now.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

/** Milliseconds until local midnight — drives the end-screen countdown. */
export function msUntilLocalMidnight(now: Date = new Date()): number {
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow.getTime() - now.getTime();
}

/** Format a millisecond duration as `H:MM:SS` for the countdown display. */
export function formatHms(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
